import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { 
  TrendingUp, 
  GitBranch, 
  Users, 
  Calendar,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const COLORS = [
  '#10b981', // green
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
];

/**
 * VersionDistributionChart - Shows version adoption rates and migration progress
 * 
 * Features:
 * - Version adoption percentage by programs and sessions
 * - Timeline showing version deployment and adoption
 * - Migration progress tracking
 * - Version comparison metrics
 */
const VersionDistributionChart = ({ data }) => {
  if (!data) {
    return (
      <div className="text-center py-4 text-gray-500">
        No version distribution data available
      </div>
    );
  }

  const chartConfig = {
    adoption: {
      label: "Adoption Rate",
      color: "#3b82f6",
    },
    programs: {
      label: "Programs",
      color: "#10b981",
    },
    sessions: {
      label: "Sessions", 
      color: "#f59e0b",
    },
  };

  return (
    <div className="space-y-6">
      {/* Version Adoption Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Adoption Rates Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart className="h-5 w-5" />
              <span>Version Adoption Rates</span>
            </CardTitle>
            <CardDescription>
              Adoption percentage across programs and sessions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.adoptionRates}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="version" 
                    tick={{ fontSize: 12 }}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <ChartTooltip 
                    content={<ChartTooltipContent />}
                    formatter={(value, name) => [
                      name === 'adoption' ? `${value}%` : value,
                      name === 'adoption' ? 'Adoption Rate' : 
                      name === 'programs' ? 'Programs Using' : 'Sessions Using'
                    ]}
                  />
                  <Bar dataKey="adoption" fill="var(--color-adoption)" name="adoption" />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Version Distribution Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <GitBranch className="h-5 w-5" />
              <span>Version Distribution</span>
            </CardTitle>
            <CardDescription>
              Current version usage across all materials
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.adoptionRates}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="adoption"
                    nameKey="version"
                  >
                    {data.adoptionRates.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
            
            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              {data.adoptionRates.map((item, index) => (
                <div key={item.version} className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-sm font-medium">{item.version}</span>
                  <span className="text-xs text-gray-500">({item.adoption}%)</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Version Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>Version Adoption Timeline</span>
          </CardTitle>
          <CardDescription>
            Version usage trends over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.versionTimeline}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <ChartTooltip 
                  content={<ChartTooltipContent />}
                  labelFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    });
                  }}
                />
                {Object.keys(data.versionTimeline[0] || {})
                  .filter(key => key !== 'date')
                  .map((version, index) => (
                    <Line 
                      key={version}
                      type="monotone" 
                      dataKey={version} 
                      stroke={COLORS[index % COLORS.length]}
                      strokeWidth={2}
                      dot={{ fill: COLORS[index % COLORS.length], strokeWidth: 2, r: 3 }}
                      name={version}
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Migration Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Version Migration Progress</span>
          </CardTitle>
          <CardDescription>
            Progress of migrating to latest versions across programs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {Object.entries(data.migrationProgress).map(([version, progress]) => (
              <div key={version} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Badge variant="secondary" className="font-mono">
                      {version}
                    </Badge>
                    <span className="text-sm font-medium text-gray-900">
                      Migration Progress
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {progress.percentage >= 80 ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : progress.percentage >= 50 ? (
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                    )}
                    <span className="text-sm font-medium">
                      {progress.migrated}/{progress.total} ({progress.percentage}%)
                    </span>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      progress.percentage >= 80 ? 'bg-green-500' :
                      progress.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
                
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Programs migrated to {version}</span>
                  <span>
                    {progress.total - progress.migrated} remaining
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Version Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Version Usage Details</span>
          </CardTitle>
          <CardDescription>
            Detailed breakdown of version adoption by programs and sessions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 font-medium">Version</th>
                  <th className="text-center p-2 font-medium">Adoption Rate</th>
                  <th className="text-center p-2 font-medium">Programs</th>
                  <th className="text-center p-2 font-medium">Sessions</th>
                  <th className="text-center p-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.adoptionRates.map((version, index) => (
                  <tr key={version.version} className="border-b hover:bg-gray-50">
                    <td className="p-2">
                      <Badge variant="secondary" className="font-mono">
                        {version.version}
                      </Badge>
                    </td>
                    <td className="text-center p-2">
                      <div className="flex items-center justify-center space-x-2">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="font-medium">{version.adoption}%</span>
                      </div>
                    </td>
                    <td className="text-center p-2">{version.programs}</td>
                    <td className="text-center p-2">{version.sessions}</td>
                    <td className="text-center p-2">
                      {version.adoption >= 70 ? (
                        <Badge className="bg-green-100 text-green-800">
                          High Adoption
                        </Badge>
                      ) : version.adoption >= 40 ? (
                        <Badge className="bg-yellow-100 text-yellow-800">
                          Medium Adoption
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800">
                          Low Adoption
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VersionDistributionChart;